
import { BrowserModule, HammerModule } from '@angular/platform-browser';
import { APP_INITIALIZER, NgModule, provideZoneChangeDetection } from '@angular/core';
import { AppComponent } from './app.component';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LayoutModule } from '@angular/cdk/layout';
import { CoreModule } from './core/core.module';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Routes, RouterModule } from '@angular/router';
import { ContactsComponent } from './contacts/components/contacts/contacts.component';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { ToastrModule } from 'ngx-toastr';
import { ModalModule, BsModalRef } from 'ngx-bootstrap/modal';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { Ng2GoogleChartsModule } from 'ng2-google-charts';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { RouteGuardService } from 'src/app/route.guard.service';
import { httpInterceptorProviders } from './http-interceptors';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DocumentListerModule } from './pages/document-lister/document-lister.module';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { AuditLogComponent } from './ledgers/components/audit-log/audit-log.component';
import { GLOBAL_VIEW_BREADCRUMB, globalViewPage } from './pages/global-view/global-view-breadcrumb';
import { providePostHogErrorHandler } from './shared/errorHandlers/posthog-error-handler';
import { MollakProxyComponent } from './internal-tools/mollak-proxy/mollak-proxy.component';
import { isAdminOrIntegrationGuard } from './internal-tools/is-admin-or-integration.guard';
import { providePrimeNG } from 'primeng/config';
import { PrimengTheme } from '../primeng-theme';
import { GoogleLoginProvider, SOCIAL_AUTH_CONFIG, SocialAuthServiceConfig, SocialLoginModule } from '@abacritt/angularx-social-login';
import { environment } from 'src/environments/environment';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { initializeKeycloak } from './keycloack-init-factory';
import { DynamicKeycloakService } from './dynamic-keycloak.service';
import { AuthGuard } from './auth/auth.guard';

const routes: Routes = [
	// UnAuth Routes
	{ path: 'forgot-password', loadChildren: () => import( './forgot-password/forgot-password.module' ).then( m => m.ForgotPasswordModule ), canActivate: [ AuthGuard, RouteGuardService ], data: { animateState: 'Forget Password' } },
	{ path: 'reset-password', loadChildren: () => import( './forgot-password/components/reset-password/reset-password.module' ).then( m => m.ResetPasswordModule ), canActivate: [  AuthGuard, RouteGuardService ] },
	{ path: 'signin', loadChildren: () => import( './sign-in/sing-in.module' ).then( m => m.SignInModule ), canActivate: [ AuthGuard ], data: { animateState: 'Sign in' } },
	// Common Routes
	{ path: 'global-view', loadChildren: () => import( './pages/global-view/global-view.module' ).then( m => m.GlobalViewModule ), canActivate: [ RouteGuardService ], data: { checkApproved: true, breadcrumb: GLOBAL_VIEW_BREADCRUMB.label, animateState: 'Global View' } },
	{ path: 'profile', loadChildren: () => import( './user-profile/user-profile.module' ).then( m => m.UserProfileModule ), canActivate: [ RouteGuardService ], data: { animateState: 'User profile' } },
	// Master User Routes
	{ path: 'clients', loadChildren: () => import( './clients/clients.module' ).then( m => m.ClientsModule ), canActivate: [ RouteGuardService ] }, 
	{ path: 'globalAdministration', loadChildren: () => import( './pages/global-administration-homepage/global-administration-homepage.module' ).then( m => m.GlobalAdministrationHomepageModule ), canActivate: [ RouteGuardService ] },
	// Admin Routes
	{ path: 'administration', loadChildren: () => import( './pages/administration-home/administration-home.module' ).then( m => m.AdministrationHomeModule ), canActivate: [ RouteGuardService ] },
	{ path: 'all-tenants', loadChildren: () => import( './pages/tenants-all/tenants-all.module' ).then( m => m.TenantsAllModule ), canActivate: [ RouteGuardService ], data: { checkAdmin: true, animateState: 'All Tenants' } },
	{ path: 'audit-log', component: AuditLogComponent, canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Audit Logs' ), animateState: 'Audit Log' } },
	{ path: 'collection', loadChildren: () => import( './collections-v2/collections-v2.module' ).then( m => m.CollectionsV2Module ), data: { ...globalViewPage( 'Collections' ), animateState: 'Collections' } },
	{ path: 'contact-ledger/:contactId', loadChildren: () => import( './routes/contact/new-contact.module' ).then( ( c ) => c.NewContactModule ) },
	{ path: 'contact-ledger', component: ContactsComponent, loadChildren: () => import( './contacts/contacts.module' ).then( m => m.ContactsModule ), canActivate: [ RouteGuardService ], data: { checkApproved: true, animateState: 'Contacts' } },
	{ path: 'dashboard-bi', loadChildren: () => import( './yellowfin-dashboard/yellowfin-dashboard.module' ).then( m => m.YellowfinDashboardModule ), canActivate: [ RouteGuardService ], data: { checkApproved: true, animateState: 'Yellowfin Dashboard' } },
	{ path: 'docs-admin', loadChildren: () => import( './docs-admin/docs-admin.module' ).then( m => m.DocsAdminModule ), canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Documents' ), animateState: 'Docs' } },
	{ path: 'lost-found-admin', loadChildren: () => import( './pages/admin-lost-found-items-list/admin-lost-found-items-list.module' ).then( m => m.AdminLostFoundItemsListModule ), canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Lost Items' ), animateState: 'lost found' } },
	{ path: 'internal/tools/mollak-proxy', component: MollakProxyComponent, canActivate: [ RouteGuardService, isAdminOrIntegrationGuard ] },
	{ path: 'procurement-settings', loadChildren: () => import( './pages/procurement/procurement.module' ).then( m => m.ProcurementModule ), data: { ...globalViewPage( 'Procurement Settings' ), animateState: 'procurement' } },
	{ path: 'projects', loadChildren: () => import( './pages/projects/projects.module' ).then( m => m.ProjectsModule ), canActivate: [ RouteGuardService ], data: { checkApproved: true, animateState: 'Projects' } },
	{ path: 'projects-mollak', loadChildren: () => import( './pages/project-list-mollak/project-list-mollak.module' ).then( m => m.ProjectListMollakModule ), canActivate: [ RouteGuardService ], data: { checkApproved: true, animateState: 'Project List Mollak' } },
	{ path: 'region', loadChildren: () => import( './pages/region/region.module' ).then( m => m.RegionModule ), canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Regions' ), checkClientAdmin: true, animateState: 'Region Page' } },
	{ path: 'request-admin', loadChildren: () => import( './request-admin/request-admin.module' ).then( m => m.RequestAdminModule ), canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Requests' ), animateState: 'Contacts' } },
	{ path: 'announcements', loadChildren: () => import( './announcements/announcements-v2.module' ).then( m => m.AnnouncementsV2Module ), canActivate: [ RouteGuardService ], data: globalViewPage( 'Announcements' ) },
	{ path: 'users', loadChildren: () => import( './users/users.module' ).then( m => m.UsersModule ), canActivate: [ RouteGuardService ], data: { checkAdmin: true, animateState: 'Users' } },
	{ path: 'visitors', loadChildren: () => import( './pages/visitors/visitors.module' ).then( m => m.VisitorsModule ), canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Visitors' ), animateState: 'visitors' } },
	{ path: 'vendor-requests', loadChildren: () => import( './pages/vendor-requests/vendor-requests.module' ).then( m => m.VendorRequestsModule ), canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Vendors' ), animateState: 'vendor requests' } },
	{ path: 'vendor-documents', loadChildren: () => import( './routes/property-management/vendor-documents/vendor-documents.module' ).then( m => m.VendorDocumentsModule ), data: { ...globalViewPage( 'Documents\' Approvals' ) } },
	// CX Routes
	{ path: 'cx/requests', loadChildren: () => import( './cx-requests/cx-requests.module' ).then( m => m.CXRequestsModule ), canActivate: [ RouteGuardService ], data: { animateState: 'CX Requests' } },
	{ path: 'community-wall', loadChildren: () => import( './community-wall/community-wall.module' ).then( m => m.CommunityWallModule ), canActivate: [ RouteGuardService ], data: { animateState: 'Community Wall' } },
	{ path: 'community-documents', loadChildren: () => import( './landlord-documents/my-documents.module' ).then( m => m.MyDocumentsListModule ), canActivate: [ RouteGuardService ], data: { animateState: 'My documents' } },
	{ path: 'event-calendar', loadChildren: () => import( './event-calendar/event-calendar.module' ).then( m => m.EventDatePickerModule ), canActivate: [ RouteGuardService ], data: { animateState: 'Event Calendar' } },
	{ path: 'helpline-faq', loadChildren: () => import( './helpline-faq/helpline-faq.module' ).then( m => m.HelplineFAQModule ), canActivate: [ RouteGuardService ], data: { animateState: 'Helpline & FAQ\'s' } },
	{ path: 'my-requests', loadChildren: () => import( './customer-requests/customer-requests.module' ).then( m => m.CustomerRequestsModule ), canActivate: [ RouteGuardService ], data: { animateState: 'My Requests' } },
	{ path: 'my-units-balances', loadChildren: () => import( './my-balance/my-balance.module' ).then( m => m.MyBalanceModule ), canActivate: [ RouteGuardService ], data: { animateState: 'Units and balances' } },
	{ path: 'my-units', loadChildren: () => import( './my-units/my-units.module' ).then( m => m.MyUnitsModule ), canActivate: [ RouteGuardService ], data: { animateState: 'My Units' } },
	{ path: 'oc-tenders', loadChildren: () => import( './oc-tenders/oc-tenders.module' ).then( m => m.OCTendersModule ), canActivate: [ RouteGuardService ], data: { animateState: 'Tenders' } },
	{ path: 'profiles-update', loadChildren: () => import( './pages/profile-update-request/profile-update-request.module' ).then( m => m.ProfileUpdateRequestModule ), canActivate: [ RouteGuardService ], data: { ...globalViewPage( 'Profile Update Requests' ), animateState: 'Contacts' } },
	{ path: 'welcome-customer', loadChildren: () => import( './welcome/contact-home-page/contact-home-page.module' ).then( m => m.ContactHomePageModule ), canActivate: [ RouteGuardService ] },
	{ path: 'contact-us', loadChildren: () => import( './pages/contact-us/contact-us.module' ).then( m => m.ContactUsModule ), data: { animateState: 'contact us' } },
	// Vendor Routes
	{ path: 'vendor-register', loadChildren: () => import( './vendors/vendor-register/vendor-register.module' ).then( m => m.VendorRegisterModule ), canActivate: [ RouteGuardService ], data: { animateState: 'Vendor Register' } },
	{ path: 'vendor-home', loadChildren: () => import( './vendor-portal/vendor-portal.module' ).then( m => m.VendorPortalModule ), canActivate: [ RouteGuardService ], data: { animateState: 'My Requests' } },
	{ path: 'vendor-profile-incomplete', loadChildren: () => import( './vendor-profile-incomplete/vendor-profile-incomplete.module' ).then( m => m.VendorProfileIncompleteModule ) },
	// Fallback Routes
	{ path: '', redirectTo: 'signin', pathMatch: 'full' },
	{ path: 'access-denied', loadChildren: () => import( './access-denied/access-denied.module' ).then( m => m.AccessDeniedModule ) },
];

@NgModule( {
		imports: [
			BrowserModule,
			KeycloakAngularModule,
			BrowserAnimationsModule,
			HammerModule,
			LayoutModule,
			CoreModule,
			FormsModule,
			ReactiveFormsModule,
			RouterModule.forRoot( routes, {} ),
			TypeaheadModule.forRoot(),
			CollapseModule.forRoot(),
			ModalModule.forRoot(),
			BsDropdownModule.forRoot(),
			BsDatepickerModule.forRoot(),
			Ng2GoogleChartsModule,
			ToastrModule.forRoot( { timeOut: 9000, positionClass: 'toast-top-right', preventDuplicates: true, newestOnTop: false } ),
			ToastModule,
			DocumentListerModule,
			DialogModule,
			ButtonModule,
			AppComponent,
			MollakProxyComponent,
			SocialLoginModule,
		],
		providers: [
			httpInterceptorProviders,
			KeycloakService,
			{
				provide: APP_INITIALIZER,
				useFactory: initializeKeycloak,
				multi: true,
				deps: [DynamicKeycloakService],
				},
			provideZoneChangeDetection( { eventCoalescing: true } ),
			MessageService,
			BsModalRef,
			providePostHogErrorHandler(),
			provideHttpClient( withInterceptorsFromDi() ),
			provideEnvironmentNgxMask(),
			providePrimeNG( {
				theme: {
					preset: PrimengTheme,
					options: {
						darkModeSelector: false,
					}
				},
				/**
					* Body-appended overlays have to clear Bootstrap 4's z-index scale: the navbar wrapper is
					* `.fixed-top` (1030, see main.component.html) and modals go up to 1050. PrimeNG defaults
					* overlay/menu to 1000, so panels were painted underneath the navbar — a multiselect opening
					* upwards had its filter input and select-all checkbox permanently unreachable (SOC-17239).
					* Note this object REPLACES the defaults instead of merging with them, so modal and tooltip
					* must be restated at their default 1100 or they resolve to undefined.
					*/
				zIndex: {
					modal: 1100,
					overlay: 1100,
					menu: 1100,
					tooltip: 1100,
				}
			} ),
			{
				provide: SOCIAL_AUTH_CONFIG,
				useValue: {
					autoLogin: false,
					providers: [
						{
							id: GoogleLoginProvider.PROVIDER_ID,
							provider: new GoogleLoginProvider(
								environment.googleAppSettings.clientId
							),
						},
					],
				} as SocialAuthServiceConfig,
			},
		],
		exports: [
			AppComponent,
			MollakProxyComponent,
		]
	} )

export class AppModule { }
